// ENG-8295 — what a HUMAN sees where a notice was written for the AGENT.
//
// Some `kind:'notice'` rows on the direct-chat rail are not messages to the
// user at all: they are the wake instruction the agent consumes. The
// scheduled-task nudge is the clearest case — second-person imperatives, a raw
// card UUID, a literal `kanban_move(...)` call — and the customer was reading it
// verbatim in the Direct Chat transcript, as though being ordered to do the work
// themselves.
//
// The obvious fix (stop writing the notice) is not available: the notice row IS
// the delivery rail. The in-session MCP push-and-consumes it, and the API's
// starvation monitor re-dispatches through the same rail. Deleting the write
// deletes the wake. So, exactly as ENG-8294 did for the run marker, the split
// happens on the READ side: `content` keeps every word the agent needs, and the
// human is shown a short third-person summary instead.
//
// Two properties this file exists to guarantee:
//
//   1. Nothing here can weaken what the agent is told. This module only ever
//      READS a payload and RETURNS display copy; no caller passes its result
//      back onto the agent's path. `content` is untouched by construction, not
//      by convention.
//   2. The classification never sniffs `content`. The rows already carry
//      `payload.kind`, a durable discriminator written by the producer, so
//      re-wording a nudge can never silently re-expose it to the user.
//
// Substitution is deliberately SERVER-side: the API read routes call this and
// send the summary IN PLACE OF `content`, rather than shipping the nudge with a
// hint that the client should hide it. A client-side fix is only as strong as
// the most careless reader of that response; substituting on the way out means
// the console cannot render the nudge because it never receives it, and neither
// the raw text nor the card/run ids in `payload` ever cross the wire.
//
// Deriving on READ (rather than stamping display copy at write time) also covers
// every row already persisted before this shipped — no migration, no backfill.

/**
 * `payload.kind` values whose `content` is written TO THE AGENT and must never
 * be shown to a user as-is.
 *
 * Deliberately an allowlist of the machine-directed kinds rather than a denylist
 * of the user-facing ones: a new notice producer is user-facing far more often
 * than not, and the failure directions are asymmetric. Forgetting to add a new
 * AGENT-directed kind here shows internal text to one customer — visible, and
 * the bug this ticket already filed. Forgetting to exclude a new USER-facing
 * kind would silently replace a real message the user needed with a canned
 * sentence, which nobody would ever report.
 */
import type { OperatorRemedy } from '../integrations/needs-reconnect.js';

export const AGENT_DIRECTED_NOTICE_KINDS = [
  'scheduled_task_nudge',
  'kanban_check',
  // ENG-8394. These two were originally cited BELOW as examples of user-facing
  // notices; reading their content builders shows the opposite. The manager
  // feedback notice opens "Your manager just completed a performance review of
  // your work" and closes "Please reflect on this feedback and act on it now";
  // the form-timeout notice ends "do NOT keep waiting for this form" and quotes
  // a raw request id and Slack channel id. Both are second-person instructions
  // to the agent and were being rendered verbatim to the customer.
  'manager_feedback',
  'form_timeout',
  // ENG-8725. The resume notice opens "The integration you asked for is now
  // connected" and instructs the agent to pick the task back up — second person,
  // written for the machine. The user learns the same fact from the connect card
  // in that conversation flipping to its connected state, so showing this row as
  // well would say it twice, the second time in prose aimed at an agent.
  'integration_connected',
  // ENG-8745. The offer RESULT receipt (not the offer card — that one is
  // written for the human and is how they decided). Its content opens "Your
  // offer was ACCEPTED", carries an `[offer_result]` machine tag, and closes by
  // instructing the agent what not to do ("do not re-raise it yourself"). The
  // human needs none of it: they are the person who tapped the button, and the
  // card swapped under their finger to say so.
  'offer_result',
  // ENG-9051. The "you are blocking another agent" notice. Its content opens
  // with a `[blocking]` machine tag and is second-person instruction to the
  // agent ("Do whatever you owe them, then tell them"). The customer has no
  // part in it - this is one agent telling another it has stopped.
  'waiting_on_agent',
  // ENG-10343 / ADR-0107 D2. An instruction a platform admin sent an agent over
  // the authenticated rail. Agent-directed by the same test as the rest: the
  // content is second-person and written for the machine.
  //
  // Classified here even though the rail mints its own session, so an operator
  // instruction should not land in a customer's thread in the first place. That
  // is a property of the WRITER, and this list is what the READER consults —
  // leaving it out would make the hiding an accident of which session someone
  // chose rather than a decision, which is the reasoning that put the payload
  // stamp on `postApprovalNotice`.
  'operator_instruction',
] as const;

export type AgentDirectedNoticeKind = (typeof AGENT_DIRECTED_NOTICE_KINDS)[number];

const AGENT_DIRECTED_KIND_SET: ReadonlySet<string> = new Set(AGENT_DIRECTED_NOTICE_KINDS);

/** Is this `payload.kind` a notice written for the agent rather than the user? */
export function isAgentDirectedNoticeKind(kind: unknown): kind is AgentDirectedNoticeKind {
  return typeof kind === 'string' && AGENT_DIRECTED_KIND_SET.has(kind);
}

/**
 * ENG-8572: whether a notice ROW should be HIDDEN from the user-facing direct
 * chat entirely, rather than shown as the ENG-8295 summary. Exactly the
 * agent-directed kinds — a notice whose `content` was written for the agent, not
 * the user (a scheduled-task nudge, a board check, a delivered performance
 * review, a form timeout). ENG-8295 summarised these ("A scheduled task
 * started: …"); a summary is still noise to an end user, so the user-facing read
 * + broadcast now drop them instead.
 *
 * Takes the whole payload (like {@link noticeDisplayText}) so a caller holding a
 * row's payload can ask directly. Classification is on `payload.kind` ONLY,
 * never the content — the same durable-discriminator guarantee ENG-8295 relies
 * on, so re-wording a nudge can never silently re-expose it. The agent's own
 * read path (`POST /host/direct-chat/poll`) does not consult this, so hiding a
 * row from the user never weakens what the agent is told.
 *
 * @deprecated ENG-9592 — NOT the audience check any more. Use
 * {@link shouldHideNoticeFromUser}, which takes the ROW and hides an unclassified
 * kind instead of showing it. This one answers the narrower question "is this one
 * of the kinds that has SUMMARY COPY?", which is all `noticeDisplayText` needs;
 * it has no production callers left. Reaching for it by name — it still reads
 * like the hide check — gets you show-by-default, which is precisely the defect
 * ENG-9592 was filed for.
 */
export function isAgentDirectedNoticePayload(payload: unknown): boolean {
  if (payload === null || typeof payload !== 'object') return false;
  return isAgentDirectedNoticeKind((payload as { kind?: unknown }).kind);
}

// ---------------------------------------------------------------------------
// ENG-9592 — the audience decision, made for EVERY kind rather than for the
// handful somebody happened to notice.
// ---------------------------------------------------------------------------
//
// WHAT WAS WRONG
//
// `AGENT_DIRECTED_NOTICE_KINDS` above calls itself an allowlist, and it is one —
// of the machine-directed kinds. But the question a reader of Direct Chat is
// asking is "should the user see this?", and against THAT question the list is a
// DENYLIST: a kind absent from it is shown. It held 8 entries while the notice
// taxonomy grew to 25, so the answer for the other 17 was "show it", arrived at
// by nobody.
//
// The nine notices in the ENG-9592 report are all in that gap. The worst reads,
// to a customer, in full:
//
//     ... The card is back on your board in todo. Pick it up (move it to
//     in_progress) and continue the task with their input ... Brad Bond is
//     reachable on slack: DM `U055SHV55NW`. Reply to them there to complete
//     their task - do NOT answer in direct-chat.
//
// — instructions to the agent, a raw Slack member id, and an order not to reply
// in the surface it is being displayed in. `waiting_response` was never
// classified, so it was shown.
//
// WHY THE DIRECTION FLIPS
//
// The comment on `AGENT_DIRECTED_NOTICE_KINDS` argues for defaulting to SHOWN,
// on asymmetric failure directions: forgetting an agent-directed kind shows
// internal text to one customer (visible, reported), while forgetting to exclude
// a user-facing kind would silently replace a real message with a canned
// sentence (never reported).
//
// That reasoning was sound WHEN IT WAS WRITTEN, because ENG-8295's consequence
// was SUBSTITUTION — a listed kind still appeared, in third person. ENG-8572
// then changed the consequence to HIDING, and the argument did not survive the
// change: both failure directions are now silent-and-invisible, so "shown" stops
// being the safe default and becomes merely the one that fails toward the
// customer. Nine notices reached a screen while the denylist reported no
// problem.
//
// So the default flips. What does NOT flip is the concern behind it, and this
// table is the answer to it: EVERY kind that exists today carries an explicit
// verdict, so no notice in service today is hidden by omission. Hidden-by-default
// governs only kinds that do not exist yet — where a new producer is held for one
// line of review rather than being published to customers unread.
//
// Each verdict below was taken by reading the producer's content builder, never
// by inferring from the kind's name. That distinction is load-bearing: the
// comment on `AGENT_DIRECTED_NOTICE_KINDS` already records `manager_feedback`
// and `form_timeout` being cited as user-facing examples until someone read them
// and found the opposite. It happened a third time here — that same comment
// still lists "form results" among the genuinely user-facing kinds, and
// `form-result-notify.ts` opens "Inject the submitted form answers into the
// agent's session" and closes "Use payload.values to continue the user's task".
// It is agent-directed, and it is classified so below.

/** Who a notice's `content` was written for. */
export type NoticeAudience = 'user' | 'agent';

/**
 * Every `payload.kind` written to `direct_chat_messages` by this build, and who
 * its `content` addresses.
 *
 * Total by construction and kept that way by
 * `scripts/check-notice-kind-audience.mjs`, which fails CI on a produced kind
 * that is missing here. Being total is the whole point: it is what lets the
 * DEFAULT be "hide" without that default silently deciding anything about a
 * notice already in service.
 *
 * `'user'` does not mean "harmless" — it means the row is addressed to the
 * person and they can act on it or asked for it. `'agent'` covers both
 * instructions to the machine and internal plumbing.
 */
export const NOTICE_KIND_AUDIENCE = {
  // ---- USER -------------------------------------------------------------
  // An integration broke and only the customer can reconnect it. Carries the
  // ENG-7652 deep link and renders as the ENG-9256 card — the one notice kind
  // that already earned a bespoke renderer.
  integration_failure: 'user',
  // The other half of that pair: the thing you were asked to reconnect is working
  // again. Kept visible even though it needs no ACTION, because it closes a loop
  // the customer was explicitly pulled into — hiding it would leave the reconnect
  // card as the last word on a problem that is now fixed.
  //
  // ENG-9592 left a wart here and recorded it: two of the three recovery arms
  // were written in the second person to the AGENT ("mention it to your owner",
  // "your session will pick it up"), which reads as nonsense to the customer now
  // seeing the same string. ENG-9609 rewrote all three to be true for both
  // readers and pinned it in `eng-9609-recovery-copy-dual-audience.test.ts`.
  // The note is deleted rather than updated: a deferral that outlives the thing
  // it defers is just a false claim about the code.
  integration_recovered: 'user',
  // The form itself. `direct-chat-form.ts` calls it "the agent→user render
  // message"; hiding it would remove the control the user is meant to fill in.
  form_request_render: 'user',
  // ENG-9592: the outcome chip for a button the user just pressed — "already
  // resolved", "only the manager can approve", the re-prompt. Its producer says
  // it exists so the click does not "look like it did nothing", which is exactly
  // an answer owed to a person. See the note on the stamped payload below.
  approval_outcome: 'user',

  // ---- AGENT ------------------------------------------------------------
  // The ENG-9592 report's headline kind: parked-card pickup instructions, in the
  // second person, ending in an order not to reply in direct-chat.
  waiting_response: 'agent',
  // Already agent-directed before this change; each is summarised above and its
  // reasoning lives beside its entry in AGENT_DIRECTED_NOTICE_KINDS.
  scheduled_task_nudge: 'agent',
  kanban_check: 'agent',
  manager_feedback: 'agent',
  form_timeout: 'agent',
  integration_connected: 'agent',
  offer_result: 'agent',
  waiting_on_agent: 'agent',
  // "The user submitted your form … Use payload.values to continue the user's
  // task" — a delivery mechanism, with a raw request uuid. The user submitted it;
  // they do not need to be told they did.
  form_result: 'agent',
  // Same shape for a tapped button: "The user tapped X on your button prompt
  // (request `<uuid>`)". They tapped it.
  button_click: 'agent',
  // ENG-9397: tells the agent a question was answered AFTER it stopped listening
  // so it does not report the person as silent. The person's own answer goes
  // back on the channel they tapped in, not here.
  late_button_click: 'agent',
  // Broker grant resolutions. Second person to the agent, and each carries the
  // grant uuid the tool contracts explicitly forbid showing a user ("NEVER paste
  // the grant UUID into user-facing prose"). Two of these are in the report.
  supabase_grant_resolved: 'agent',
  supabase_grant_denied: 'agent',
  aws_grant_resolved: 'agent',
  aws_grant_denied: 'agent',
  gcp_grant_resolved: 'agent',
  gcp_grant_denied: 'agent',
  // HITL tool-call approvals: "Do NOT call this tool again, do NOT claim the
  // action happened, and do NOT poll".
  integration_invoke_resolved: 'agent',
  // ADR-0104: the batch finished. Addressed to the agent, which reports the
  // counts onward; the approver is told separately, on the surface they
  // approved from.
  integration_batch_resolved: 'agent',
  integration_invoke_denied: 'agent',
  // Support/recruit approvals — same shape, agent resumes the provisioning.
  recruit_resolved: 'agent',
  recruit_denied: 'agent',
  // "Your `<verb>` approval request (`<uuid>`) EXPIRED at …" — in the report,
  // uuid and all.
  approval_expired: 'agent',
  // A routine fired; the agent picks the work up.
  routine_firing_result: 'agent',
  // Live-advisor wake: "Call `live_read` with escalation_id …" — a literal tool
  // call addressed to the machine.
  live_advisor_escalation: 'agent',
  live_advisor_session_open: 'agent',
  // ENG-9631's counterpart to the open briefing, and agent-directed for the
  // same reason: `buildSessionCloseMessage` writes in the second person to the
  // machine — "the sales call YOU were advising on", "you will not be woken
  // again". Read the builder, as this file asks. The person already knows they
  // pressed Stop; the one who does not know is the agent.
  live_advisor_session_close: 'agent',
  // The assigner reopened a card and the agent must work it again.
  assigner_reopen: 'agent',
  // An inbound trigger event delivered onto the rail for the agent to handle.
  // Named `trigger_event` by `triggerDeliveryPayload` — NOT `trigger_delivery`,
  // which is what the producing module is called. Read from the builder.
  trigger_event: 'agent',
  // Operational degradation routed into the agent's chat by the alert bridge.
  degradation_alert: 'agent',
  // ENG-9592: probes and synthetic prompts, previously carrying NO payload at
  // all and therefore unclassifiable. Stamped at their producers so the verdict
  // is recorded rather than inherited from the default. `agent_probe` covers
  // both probe kinds (liveness and the board nudge); the specific one is
  // `payload.probe_kind`, so a new probe kind needs no entry here.
  agent_probe: 'agent',
  agent_question_probe: 'agent',
  kanban_confirm_prompt: 'agent',
  // ENG-10343 / ADR-0107 D2. A platform admin's instruction, delivered over the
  // authenticated rail. Addressed to the machine, and the only notice kind whose
  // authorship the agent is told it can rely on — see `operatorInstructionHeader`
  // in packages/mcp/src/direct-chat-history.ts, which is where that reliance is
  // actually expressed. This entry only decides the audience.
  operator_instruction: 'agent',
} as const satisfies Record<string, NoticeAudience>;

export type KnownNoticeKind = keyof typeof NOTICE_KIND_AUDIENCE;

const USER_FACING_KIND_SET: ReadonlySet<string> = new Set(
  Object.entries(NOTICE_KIND_AUDIENCE)
    .filter(([, audience]) => audience === 'user')
    .map(([kind]) => kind),
);

/**
 * The kinds a user may see. Derived from {@link NOTICE_KIND_AUDIENCE} rather
 * than written out again, so the table cannot disagree with itself.
 */
export const USER_FACING_NOTICE_KINDS: readonly string[] = Object.freeze([
  ...USER_FACING_KIND_SET,
]);

/** Is this `payload.kind` one a user may see? Unknown kinds are NOT. */
export function isUserFacingNoticeKind(kind: unknown): boolean {
  return typeof kind === 'string' && USER_FACING_KIND_SET.has(kind);
}

/**
 * The known kinds a user never sees — for callers that must NAME what they are
 * excluding, which today is the `direct_chat_recent_for_user` RPC sizing its
 * fetch window (ENG-8712).
 *
 * A HINT, NOT THE AUTHORITY, and the distinction matters: hiding is
 * `shouldHideNoticeFromUser`, which also withholds a kind that is not in
 * {@link NOTICE_KIND_AUDIENCE} at all. This list cannot express that — there is
 * no way to enumerate the kinds a future build will invent — so a row of an
 * unknown kind is inside the RPC's window and dropped afterwards. That costs a
 * window slot; it never shows the row.
 */
export const HIDDEN_NOTICE_KINDS: readonly string[] = Object.freeze(
  Object.entries(NOTICE_KIND_AUDIENCE)
    .filter(([, audience]) => audience === 'agent')
    .map(([kind]) => kind),
);

/**
 * ENG-9592: whether a ROW must be withheld from the user-facing direct chat.
 * Replaces {@link isAgentDirectedNoticePayload} at every user-facing read and
 * broadcast.
 *
 * A notice is hidden unless its kind is explicitly `'user'` in
 * {@link NOTICE_KIND_AUDIENCE} — so a notice kind that ships without an audience
 * decision is withheld until someone makes one, instead of appearing on a
 * customer's screen unreviewed.
 *
 * A NOTICE WITH NO `payload.kind` IS SHOWN, and that is not a hole — it is the
 * boundary of what this decision can be made about.
 *
 * Hide-by-default is a rule about KINDS. A row with no kind has not been
 * classified badly; it has not been classified at all, and the thing that writes
 * most of them is not in this repository's deploy. `POST /host/direct-chat/reply`
 * accepts `kind: 'notice'` from the HOST (ENG-8274), and the host-side producers
 * in `packages/mcp` send no payload with it. Several are the most user-facing
 * messages in the product:
 *
 *     "The Ninjafy platform is offline for scheduled maintenance right
 *      now, so I can't pick this up — please try again shortly."
 *     "There is an issue with your account, please contact support@augmented.team."
 *
 * Withholding those means a customer messages their agent and gets SILENCE —
 * including, in the second case, silence instead of the sentence that tells them
 * how to get their agent back. That is a worse bug than the one being fixed, and
 * it fails in the same invisible way.
 *
 * Stamping those producers would not close it either. They ship in the CLI
 * bundle and reach the fleet by host self-update, so payload-less notices keep
 * arriving from not-yet-converged hosts for an unbounded window. A rule whose
 * correctness depends on fleet convergence is not a rule, it is a race.
 *
 * So the API-side producers that were payload-less were stamped instead
 * (`agent_probe`, `agent_question_probe`, `kanban_confirm_prompt`,
 * `approval_outcome`) — that is what makes THEM classifiable — and a remaining
 * payload-less notice is taken for what it is: a host-posted message to a person.
 *
 * THE ROW'S OWN `kind` COLUMN IS DELIBERATELY NOT CONSULTED.
 *
 * Gating on `row.kind === 'notice'` is tempting — these ARE notices, and it reads
 * like a free safety belt. It is a hole, and it was one in this function's first
 * draft (caught in review on #5116):
 *
 *   - `integration-connected-notify.ts` stamps `payload.kind:
 *     'integration_connected'` and NO top-level kind, so the column defaults to
 *     `'message'`. Its content reads "Do not call them or report them missing"
 *     and "Pick up the task you paused". A row-kind gate shows that to the
 *     customer — the exact class of text this change exists to remove,
 *     reintroduced by the change removing it. That file's own comment says "the
 *     payload kind is what marks it agent-directed, and that is what keeps it out
 *     of the console", which was true until the gate made it false.
 *   - `waiting-response.ts` sets its row kind DYNAMICALLY: `'notice'` on the arms
 *     that send the agent elsewhere, `'message'` on the arms it must answer in
 *     direct-chat (ENG-9305 — forcing notice there gets the row swept to
 *     `failed`). Both arms carry the same agent-directed content. A row-kind gate
 *     hides the reported bug on one arm and leaves it on the other.
 *
 * It also put this out of step with the database: `direct_chat_recent_for_user`
 * filters `coalesce(m.payload ->> 'kind', '')` with no reference to `m.kind` at
 * all, so the SQL window and this redaction disagreed about the same row.
 *
 * WHAT PROTECTS ORDINARY CONVERSATION IS THE PAYLOAD CHECK, NOT THE ROW KIND.
 *
 * The predicate this replaces could be handed anything, because an unrecognised
 * payload meant `false` — show it. This one defaults the other way, and the call
 * sites feed it EVERY message in the window. So the first branch below is
 * load-bearing: a human turn carries no payload and is shown, and so is a
 * host-posted notice. Without it the transcript comes back empty for everyone,
 * with nothing thrown and nothing logged.
 *
 * The parameter is a ROW rather than a bare payload so the rename-shaped swap
 * `shouldHideNoticeFromUser(payload)` does not compile, and `payload` is a
 * REQUIRED key for the same reason — with it optional, any object satisfies the
 * type and the compiler waves the mistake through.
 *
 * Everything the module header guarantees still holds. Classification reads
 * `payload.kind` and never `content`, so re-wording a notice cannot change who
 * sees it; and the agent's own read path (`POST /host/direct-chat/poll`) does
 * not consult this at all, so nothing here can weaken what the agent is told.
 */
export function shouldHideNoticeFromUser(row: { payload: unknown }): boolean {
  const payload = row.payload;
  // No payload, or no discriminator on it: an ordinary turn, or a host-posted
  // notice. Shown — see above for why this is the boundary of the decision
  // rather than a gap in it.
  if (payload === null || typeof payload !== 'object') return false;
  const kind = (payload as { kind?: unknown }).kind;
  if (typeof kind !== 'string' || kind.length === 0) return false;
  // A kind exists. Now hide-by-default applies: shown only if classified 'user'.
  return !isUserFacingNoticeKind(kind);
}

/**
 * Shown when a scheduled-task nudge carries no usable task name — which is every
 * row written before ENG-8295, since `task_name` was not stamped then. It says
 * strictly less than the named form, never anything false.
 */
const SCHEDULED_TASK_FALLBACK = 'A scheduled task started.';

/** The board-check nudge has nothing task-specific to name; one sentence covers it. */
const KANBAN_CHECK_SUMMARY = 'Checking the task board.';

/**
 * The manager-feedback notice body is the review itself — ratings and the
 * manager's own note — addressed to the agent in the second person. The customer
 * WROTE that review, so there is nothing to tell them here beyond the fact that
 * it reached the agent; the review's real home is the performance-review surface.
 */
const MANAGER_FEEDBACK_SUMMARY = 'A performance review was delivered to this agent.';

/** Said when a form-timeout notice carries no usable title. */
const FORM_TIMEOUT_FALLBACK = 'A form expired without a response.';

/**
 * ENG-8725. Deliberately says only that the connection landed: the customer
 * already learns WHICH integration from the connect card in that conversation
 * flipping to its connected state, and this string exists only for a path that
 * surfaces the row anyway (today none does — both the REST read and the
 * broadcast drop agent-directed rows outright).
 */
const INTEGRATION_CONNECTED_SUMMARY = 'An integration finished connecting.';

/**
 * ENG-8745. Says only that the decision reached the agent. The customer made
 * the decision themselves one tap ago and the offer card already swapped to
 * confirm it, so naming the routine again here would just say it twice — and
 * the routine's real home is the routines surface.
 */
const OFFER_ACCEPTED_SUMMARY = 'A routine offer was accepted.';
const OFFER_SNOOZED_SUMMARY = 'A routine offer was snoozed.';
const OFFER_DISMISSED_SUMMARY = 'A routine offer was dismissed.';

/**
 * Said when the receipt carries a resolution this build does not know (a
 * future verb, or a truncated row). Deliberately a real sentence rather than
 * null: this module's two consumers must agree that a registered kind is BOTH
 * hidden and summarised, and the suite pins that equivalence. It also says
 * strictly less than the three named forms rather than anything false - the
 * receipt only exists because the offer WAS resolved.
 */
const OFFER_RESULT_FALLBACK = 'A routine offer was resolved.';

/**
 * ENG-9051. Names neither agent: the customer is not a participant in one
 * agent's dependency on another, and the two code names would read as noise.
 * Exists only so the kind is summarised rather than null - both the REST read
 * and the broadcast drop agent-directed rows outright today.
 */
const WAITING_ON_AGENT_SUMMARY = 'An agent is waiting on another agent.';
/**
 * ENG-10343. Says that support acted, and nothing about what was asked.
 *
 * The rail mints its own session, so in practice nobody reaches this string. It
 * is deliberately non-null anyway: a registered kind that returns null drifts
 * out of step with `isAgentDirectedNoticePayload`, an equivalence this module's
 * suite pins — and if an operator instruction ever DID reach a customer thread,
 * the honest failure is a bland sentence, not the instruction's own text.
 *
 * Whether the customer should be told more than this is not settled here; it is
 * the same question as ENG-10570's third criterion.
 */
const OPERATOR_INSTRUCTION_SUMMARY = 'Ninjafy support sent this agent an instruction.';

/**
 * A quoted name (a task name, a form title) is the customer's own text, so it is
 * quoted rather than trusted as prose — but it still reaches a chat surface, so
 * bound it. The cap is generous (these names are short by nature) and exists to
 * stop a pathological one from reproducing the very wall of text this ticket
 * removed.
 */
const MAX_QUOTED_NAME_CHARS = 80;

function cleanQuotedName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // Collapse newlines/tabs: the summary is a single line, and a name carrying
  // its own line breaks would reopen the multi-line notice this replaced.
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (collapsed.length === 0) return null;
  return collapsed.length > MAX_QUOTED_NAME_CHARS
    ? `${collapsed.slice(0, MAX_QUOTED_NAME_CHARS - 1)}…`
    : collapsed;
}

/**
 * The user-facing summary for a notice row, or `null` when the row should render
 * its own `content` unchanged.
 *
 * `null` is the answer for every user-facing notice (integration failure and
 * recovery, offers, form results) and every ordinary message — the overwhelmingly
 * common case — so a caller that substitutes only on a non-null result leaves
 * today's behaviour byte-identical everywhere but the agent-directed kinds.
 *
 * Takes the whole payload (not a pre-extracted kind) so the classification and
 * the copy stay in one place; a caller cannot accidentally pair one kind's
 * verdict with another's wording.
 */
export function noticeDisplayText(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') return null;
  const kind = (payload as { kind?: unknown }).kind;
  if (!isAgentDirectedNoticeKind(kind)) return null;

  if (kind === 'kanban_check') return KANBAN_CHECK_SUMMARY;
  if (kind === 'manager_feedback') return MANAGER_FEEDBACK_SUMMARY;
  if (kind === 'integration_connected') return INTEGRATION_CONNECTED_SUMMARY;
  if (kind === 'waiting_on_agent') return WAITING_ON_AGENT_SUMMARY;
  if (kind === 'operator_instruction') return OPERATOR_INSTRUCTION_SUMMARY;

  // ENG-8745. Named per resolution rather than one generic sentence, with an
  // unknown value falling back to the resolution-agnostic form instead of
  // asserting an outcome that did not happen. NOT null: a registered kind must
  // stay summarised, or it drifts out of step with `isAgentDirectedNoticePayload`
  // (which hides it) — an equivalence this module's suite pins.
  if (kind === 'offer_result') {
    const resolution = (payload as { resolution?: unknown }).resolution;
    if (resolution === 'accepted') return OFFER_ACCEPTED_SUMMARY;
    if (resolution === 'snoozed') return OFFER_SNOOZED_SUMMARY;
    if (resolution === 'dismissed') return OFFER_DISMISSED_SUMMARY;
    return OFFER_RESULT_FALLBACK;
  }

  if (kind === 'form_timeout') {
    const title = cleanQuotedName((payload as { title?: unknown }).title);
    return title === null
      ? FORM_TIMEOUT_FALLBACK
      : `A form expired without a response: “${title}”.`;
  }

  // ENG-8725: `scheduled_task_nudge` is now NAMED rather than being whatever
  // falls off the end.
  //
  // The tail used to read "anything still here is a scheduled task", which is
  // true only until someone adds a kind to AGENT_DIRECTED_NOTICE_KINDS — and
  // then it is silently FALSE: the new kind inherits “A scheduled task started”,
  // a sentence about something that never happened, with no error and no failing
  // test. That is exactly what `integration_connected` did on the way in
  // (CodeRabbit, PR #4461). Naming the kind moves the cost of the next addition
  // from a wrong sentence to a null.
  if (kind === 'scheduled_task_nudge') {
    const taskName = cleanQuotedName((payload as { task_name?: unknown }).task_name);
    return taskName === null
      ? SCHEDULED_TASK_FALLBACK
      : `A scheduled task started: “${taskName}”.`;
  }

  // An agent-directed kind with no branch here. Null means "no substitute", and
  // callers fall back to the row's own content — so this is NOT a silent-safe
  // default, it is a loud one: `redactedContent` would pass agent-directed prose
  // through, which is the ENG-8295 bug and gets noticed. A confidently wrong
  // summary would not be.
  return null;
}

/**
 * ENG-8728: the ONE field a notice's `payload` may expose to the browser — the
 * deep link that reconnects a broken integration.
 *
 * Why this exists rather than simply shipping `payload`. The producer already
 * computes an ENG-7652 one-click reconnect URL and stores it on the row
 * (`payload.reconnect_url`), and the Slack leg already renders it — but the read
 * routes strip `payload` wholesale before serving, deliberately, because it
 * carries card / task / run ids the browser has no business seeing. So the URL
 * was built, persisted, and then discarded one layer short of the UI, leaving
 * the customer a prose breadcrumb telling them to navigate by hand to a place we
 * already had a direct link to.
 *
 * The fix is an allowlist of exactly one field, NOT a relaxation of the strip.
 * Extraction lives here beside {@link noticeDisplayText} for the same reason
 * that function takes a whole payload: the classification and the data it
 * licenses stay in one place, so a caller cannot pair one kind's verdict with
 * another kind's payload.
 *
 * Returns null unless the row is an integration-failure notice carrying a
 * well-formed absolute http(s) URL. A non-http value is REFUSED rather than
 * passed through: this string becomes an `href`, and `javascript:` is the first
 * thing a payload-poisoning attempt would reach for. Null is also the answer for
 * every row written before the producer stamped a URL, and for a scope with no
 * agent to deep-link to — in all of which the caller keeps rendering the prose
 * breadcrumb it renders today.
 */
export function noticeReconnectUrl(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') return null;
  const p = payload as { kind?: unknown; reconnect_url?: unknown };
  if (p.kind !== 'integration_failure') return null;
  const raw = p.reconnect_url;
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : null;
}

/**
 * ENG-9256 — the branding an integration-failure notice needs to render as a
 * CARD rather than a truncated line with an underlined link.
 *
 * Deliberately a sibling of {@link noticeReconnectUrl} and not a replacement.
 * That function is the allow-list chokepoint for the one field that becomes an
 * `href`, and it keeps sole ownership of the http(s) check; this one adds the
 * display fields and DELEGATES the URL to it, so there is exactly one place that
 * decides whether a reconnect link is safe to render.
 *
 * Returns null unless there is a usable URL: a card with no button is strictly
 * worse than the prose breadcrumb already in `content`, because it looks like a
 * control that failed rather than a sentence you can act on.
 */
export interface NoticeReconnectRef {
  definition_id: string;
  /** Catalog product label, e.g. `Outlook`. Never the `composio/outlook` slug. */
  display_name: string | null;
  /** Catalog logo URL; null renders an initials tile. */
  icon: string | null;
  /** Which failure this is, which decides the card's copy. */
  failure_kind: string | null;
  reconnect_url: string;
  /**
   * ENG-9890: WHICH remedy fixes it, so the card can stop telling the seventeen
   * native api_key toolkits to "sign in again" when no sign-in exists.
   *
   * Three-valued on purpose, and the third value is `undefined`. Absent means the
   * row predates this field, so the card falls back to its OAuth reading and
   * renders exactly what it rendered when the row was written. Explicit `null`
   * means resolved-to-nothing: no self-serve fix, an engineer is needed.
   */
  remedy?: OperatorRemedy;
}

export function noticeReconnectRef(payload: unknown): NoticeReconnectRef | null {
  const url = noticeReconnectUrl(payload);
  if (!url) return null;
  const p = payload as {
    definition_id?: unknown;
    display_name?: unknown;
    icon?: unknown;
    failure_kind?: unknown;
    remedy?: unknown;
  };
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim().length > 0 ? v : null;
  // ENG-9890: preserve the absent/null distinction the card depends on. An
  // unrecognised string degrades to ABSENT rather than null — a value we do not
  // understand must not be read as "we established there is no fix", which would
  // swap a working Reconnect button for an escalation notice.
  const remedy: OperatorRemedy | undefined =
    p.remedy === 'token-paste' || p.remedy === 'oauth-reconnect'
      ? p.remedy
      : p.remedy === null
        ? null
        : undefined;
  return {
    definition_id: str(p.definition_id) ?? '',
    display_name: str(p.display_name),
    icon: str(p.icon),
    failure_kind: str(p.failure_kind),
    reconnect_url: url,
    remedy,
  };
}

/**
 * ENG-9864 (ADR-0037 S2): the render-safe half of a `form_request_render`
 * payload, lifted for the Direct Chat console.
 *
 * WHY THIS IS A SIBLING OF {@link noticeReconnectRef} RATHER THAN A NEW ROUTE.
 * `redactAgentDirectedNotices` strips `payload` wholesale at one destructure,
 * and its own comment is explicit that anything escaping must be lifted THERE
 * so the strip and the allow-list can never disagree about what gets out. A
 * form card needs schema + title in the browser, so it goes through the same
 * chokepoint as an allow-listed shape rather than around it.
 *
 * WHAT IS DELIBERATELY NOT HERE. `status`. Whether a form is still answerable
 * has to be read LIVE at request time, for the same reason the approval and
 * connect cards read theirs live: between the row being written and a reload
 * rendering it, the form may have been answered in another tab or swept to
 * `timeout` by the cron. A status frozen onto the message would keep offering a
 * Submit button that cannot land — a control that lies is worse than no
 * control. The caller pairs this ref with a live lookup.
 *
 * Returns null unless the payload really is a form with a usable schema: a card
 * with no fields would render as an empty box the person cannot act on or
 * dismiss.
 */
export interface DirectChatFormRef {
  /** pending_interaction callback_id — the id the submission posts against. */
  interaction_id: string;
  title: string;
  /** The constrained JSON Schema, straight from the agent-supplied snapshot. */
  schema: Record<string, unknown>;
  /** Render-only layout hints; never affects validation. */
  ui_schema: Record<string, unknown> | null;
}

export function directChatFormRef(payload: unknown): DirectChatFormRef | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as {
    kind?: unknown;
    interaction_id?: unknown;
    title?: unknown;
    schema?: unknown;
    ui_schema?: unknown;
  };
  if (p.kind !== 'form_request_render') return null;
  if (typeof p.interaction_id !== 'string' || p.interaction_id.trim() === '') return null;
  // A schema must be a plain object with at least one property. An empty or
  // malformed one cannot produce a form anybody can fill in, and rendering the
  // shell anyway leaves a dead card in the transcript forever.
  const schema = p.schema;
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return null;
  const props = (schema as { properties?: unknown }).properties;
  if (!props || typeof props !== 'object' || Array.isArray(props)) return null;
  if (Object.keys(props as Record<string, unknown>).length === 0) return null;

  const uiSchema =
    p.ui_schema && typeof p.ui_schema === 'object' && !Array.isArray(p.ui_schema)
      ? (p.ui_schema as Record<string, unknown>)
      : null;

  return {
    interaction_id: p.interaction_id,
    title: typeof p.title === 'string' && p.title.trim() !== '' ? p.title : 'Please answer',
    schema: schema as Record<string, unknown>,
    ui_schema: uiSchema,
  };
}
